import { useCallback } from 'react';

import { type SubmitHandler, useForm } from 'react-hook-form';

import { updateVariable } from '@polyai/stream/store/reducers/variables';
import { selectVariables } from '@polyai/stream/store/reducers/variables/selectors';
import { ConfirmationModal } from '@polyai/ui/components/molecules/ConfirmationModal';

import { useServerDispatch } from 'components/organisms/AgentStream/context/AgentStreamContext';
import { useAppSelector } from 'hooks/reduxHooks';
import { useToast } from 'hooks/useToast';
import { validateVariableName } from './utils';
import VariableModalContent from './VariableModalContent';

interface VariableEditPopupProps {
  variableId: string;
  onClose: () => void;
}

interface EditVariableForm {
  name: string;
}

export function VariableEditPopup({
  variableId,
  onClose,
}: VariableEditPopupProps) {
  const serverDispatch = useServerDispatch();
  const variables = useAppSelector(selectVariables);
  const variable = variables?.find(v => v.id === variableId);
  const toast = useToast();

  const {
    register,
    formState: { isValid, errors },
    handleSubmit,
  } = useForm<EditVariableForm>({
    mode: 'onChange',
    defaultValues: {
      name: variable?.name ?? '',
    },
  });

  const validate = (name: string) =>
    validateVariableName(name, variables, variableId);

  const onSubmit: SubmitHandler<EditVariableForm> = useCallback(
    data => {
      if (!isValid) {
        return;
      }
      void serverDispatch(
        updateVariable({
          id: variableId,
          name: data.name,
        }),
      ).catch(() => {
        toast.error({
          title: 'Failed to update variable',
        });
      });

      onClose();
    },
    [isValid, onClose, serverDispatch, variableId, toast],
  );

  if (!variable) {
    return null;
  }

  return (
    <ConfirmationModal
      buttonText="Save"
      cancelText="Cancel"
      confirmDisabled={!isValid}
      content={
        <VariableModalContent
          errorMessage={errors.name?.message}
          inputTestId="edit-variable-name-input"
          register={register('name', {
            validate,
            required: {
              message: 'Variable name is required',
              value: true,
            },
          })}
        />
      }
      size="md"
      subtitle="Variable will be re-named in any function where it was referenced."
      title="Edit variable"
      open
      skipCloseAndConfirm
      onCancelButtonClick={onClose}
      onClose={onClose}
      onConfirmButtonClick={handleSubmit(onSubmit)}
    />
  );
}

export default VariableEditPopup;
